import { createRoot } from "react-dom/client";
import React from "react";
import { QueryClientProvider } from "@tanstack/react-query";

// Foundation 6.3.1 grid/base styles, vendored so first paint does not wait on a
// cross-origin stylesheet. Imported first so app styles keep overriding it.
import "./styles/vendor/foundation-6.3.1.min.css";
import "react-loading-skeleton/dist/skeleton.css";
import "../node_modules/react-responsive-modal/styles.css";
import "./styles/global.scss";

import { initAdobeAdapter } from "./analytics/adobeAdapter";
import App from "./screens/app/App";
import { createAppRouter } from "./screens/app/routes";
import queryClient from "./queryClient";
import {
  captureDomainHint,
  stripDomainHintFromUrl,
} from "@services/auth/providers/entra/domainHint";
import { markAppBoot } from "./services/perf/loginTimeline";
import { AUTH_ROUTE_PATHS } from "./services/auth/authRouteConfig";
import {
  acquireAuthSession,
  initAuthRuntime,
  setAuthRouter,
} from "./services/auth/authSessionRuntime";
import {
  completeSilentRenewFrame,
  isSilentRenewFrame,
} from "./services/auth/silentRenewFrame";
import "./objectFitPolyfill/objectFitPolyfill.min.js";

// Routes rendered outside the app shell (see appRoutes).
const SHELL_LESS_PATHS = new Set([
  AUTH_ROUTE_PATHS.LOGIN,
  AUTH_ROUTE_PATHS.AUTH_CALLBACK,
  AUTH_ROUTE_PATHS.LOGOUT_CALLBACK,
]);

// Silent renew loads this same document in a hidden iframe. It must hand its
// URL to the parent and stop — booting the app there would race the parent for
// the OIDC state and can sign the real tab out. See silentRenewFrame. This is
// checked before anything else runs so the frame costs no analytics adapter, no
// React tree and no API calls.
if (isSilentRenewFrame()) {
  completeSilentRenewFrame();
} else {
  // Local/dev only, and deliberately the first thing the real boot does: it
  // backdates a mark to this document's navigation start, so the cost of
  // fetching and parsing the bundle is attributed before anything below adds to
  // it. Inside the else so the silent renew frame stays as cheap as the comment
  // above promises. See services/perf/loginTimeline.
  markAppBoot();

  initAdobeAdapter();

  // Read ?domain_hint= from the landing URL here, at boot, because this is the
  // last point at which it is guaranteed intact — the router rewrites the URL as
  // soon as it mounts, and sign-in does not redirect until several navigations
  // later. See services/auth/providers/entra/domainHint.
  captureDomainHint();
  // Then take it back out of the address bar, before the router is created and
  // the sign-in check snapshots the URL as the post-login destination.
  stripDomainHintFromUrl();

  // Creates the UserManager now rather than after the first render. When the
  // first page is inside the app shell, its sign-in session starts here too, so
  // it settles while the page's chunk downloads instead of after it. The
  // shell-less auth routes get one only if they navigate into the shell.
  // Before the router: creating it starts the first navigation's middleware.
  initAuthRuntime();
  if (!SHELL_LESS_PATHS.has(globalThis.location.pathname)) {
    acquireAuthSession();
  }

  // After the strip above: the router reads the URL when it is created.
  const router = createAppRouter();
  setAuthRouter(router);

  const container = document.getElementById("root");
  const root = createRoot(container);

  root.render(
    React.createElement(
      QueryClientProvider,
      { client: queryClient },
      React.createElement(App, { router }),
    ),
  );
}
